import { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useModal } from 'mui-modal-provider';

import { OrderPrice } from '../../../../../../graphql/order/models/get-orders.models';
import { Tooltip } from '../../../../../common/tooltip';
import { IconButton } from '../../../../../common/icon-button';
import { IconTypes, IconSizes } from '../../../../../common/icon/icon.models';
import { BonusPointsModal } from './bonus-points-modal';
import { useStyles } from './order-price-summary.styles';

interface OrderPriceSummaryProps {
  orderPrice: OrderPrice;
  points?: number;
  packageNumber?: number;
}

export const OrderPriceSummary = ({ orderPrice, points, packageNumber }: OrderPriceSummaryProps): JSX.Element => {
  const { discount, deliveryDiscount, orderPriceWithDiscount, orderPriceWithoutDiscount } = orderPrice;
  const classes = useStyles();
  const intl = useIntl();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { showModal } = useModal();

  const handleBonusInfoClick = useCallback(() => {
    if (points !== undefined) {
      showModal(BonusPointsModal, { points, packageNumber });
    }
  }, [points, packageNumber, showModal]);

  return (
  <div data-testid="order-price-summary" className={classes.container}>
    <div className={classes.totalRow}>
      <p>
        <FormattedMessage id="order.delivery.total.title" />
      </p>
      <p>{orderPriceWithDiscount} ₽</p>
    </div>
    <div className={classes.row}>
      <p>
        <FormattedMessage id="order.delivery.total.orderCost" />
      </p>
      <p className={classes.value}>
        {orderPriceWithoutDiscount} ₽
      </p>
    </div>
    {discount && (
      <div className={classes.row}>
        <p>
          <FormattedMessage id="order.delivery.total.coupon" />
        </p>
        <p className={classes.discount}>
          −{discount} ₽
        </p>
      </div>
    )}
    {deliveryDiscount && (
      <div className={classes.row}>
        <p>
          <FormattedMessage id="order.delivery.total.deliveryDiscount" />
        </p>
        <p className={classes.discount}>
          −{deliveryDiscount} ₽
        </p>
      </div>
    )}
    <div className={classes.row}>
      <p>
        <FormattedMessage id="order.delivery.total.delivery" />
      </p>
      <p><FormattedMessage id="order.delivery.total.deliveryFree" /></p>
    </div>
    {points !== undefined && (
      <div className={classes.row}>
        <p className={classes.pointsLabel}>
          <FormattedMessage id="order.delivery.total.points" />
          {points > 0 && (isMobile ? (
            <IconButton
              data-testid="points-info-icon"
              aria-label={intl.formatMessage({ id: 'order.delivery.total.points.infoAria' })}
              onClick={handleBonusInfoClick}
              iconProps={{ type: IconTypes.info, size: IconSizes.xs }}
            />
          ) : (
            <Tooltip
              title={(
                <FormattedMessage
                  id="order.delivery.total.pointsTooltip"
                  values={{ points, packageNumber }}
                />
              )}
              withArrow
              placement="bottom"
              tooltipClass={classes.pointsTooltip}
            >
              <IconButton
                data-testid="points-info-icon"
                aria-label={intl.formatMessage({ id: 'order.delivery.total.points.infoAria' })}
                iconProps={{ type: IconTypes.info, size: IconSizes.xs }}
              />
            </Tooltip>
          ))}
        </p>
        <p>{points}</p>
      </div>
    )}
  </div>
  );
};
